// Shared formatters for the Compras (shopping) surface. Extracted so
// `<ShoppingList />`, `<IngredientRow />`, `<ComprasActionBar />`, etc., don't
// each redeclare the same helpers.

export function formatCents(value: number): string {
  return `S/ ${(value / 100).toFixed(2)}`;
}

export function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** "850 g", "1 kg", "1.5 kg", "1.13 kg": kilos to the nearest 10 g, enough to shop with. */
export function formatGrams(g: number): string {
  if (g >= 1000) {
    return `${Number((g / 1000).toFixed(2))} kg`;
  }
  return `${formatNumber(g)} g`;
}

// Pretty-print the size of a store cart product based on the saved mapping,
// optionally scaled by quantity. Returns null when the mapping doesn't carry
// enough conversion data to express a size (e.g. a stray mapping with no
// packageAmount/unitsPerSku). The cart line shows this directly below the
// price so the user can see how much they're actually buying.
export function formatProductSize(
  mapping: {
    conversionMode: "measurable" | "grouped-units";
    measurementUnit: string;
    unitMultiplier: number;
    packageAmount: number | null;
    packageUnit: string | null;
    unitsPerSku: number | null;
  },
  multiplier = 1,
): string | null {
  if (mapping.conversionMode === "grouped-units") {
    const ups = mapping.unitsPerSku ?? 0;
    if (ups <= 0) return null;
    const total = ups * multiplier;
    return `${formatNumber(total)} ${total === 1 ? "und" : "und"}`;
  }
  const mu = mapping.measurementUnit.toLowerCase();
  if (mu === "kg" && mapping.unitMultiplier > 0) {
    return formatGrams(mapping.unitMultiplier * 1000 * multiplier);
  }
  const pu = mapping.packageUnit?.toLowerCase();
  const pa = mapping.packageAmount ?? 0;
  if (pa <= 0 || !pu) return null;
  if (pu === "kg") return formatGrams(pa * 1000 * multiplier);
  if (pu === "g") return formatGrams(pa * multiplier);
  if (pu === "ml") {
    const total = pa * multiplier;
    return total >= 1000 ? `${formatNumber(total / 1000)} L` : `${formatNumber(total)} ml`;
  }
  if (pu === "l") {
    const total = pa * multiplier;
    return total < 1 ? `${formatNumber(total * 1000)} ml` : `${formatNumber(total)} L`;
  }
  return `${formatNumber(pa * multiplier)} ${pu}`;
}

// Standard focus ring used on every interactive element that isn't already
// styled via the shadcn <Button /> component. Keeps WCAG 2.4.7 satisfied.
export const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";
